<template>
  <div class="mt-6 px-4 flex justify-between items-center w-full">
    <div>
      <h1 id="agent-styles-title" class="text-2xl text-gray-900 dark:text-gray-200 font-medium">
        {{ t('agentStyles.title', 'Agent Styles') }}
      </h1>
      <h1 id="agent-styles-subtitle" class="text text-gray-900 dark:text-gray-200">
        {{ t('agentStyles.subtitle', 'Create and manage styles for your agents.') }}
      </h1>
    </div>

    <div class="flex items-center space-x-4 rtl:space-x-reverse">
      <label id="search-styles-label" class="input input-bordered flex items-center" style="width:330px">
        <input id="search-styles-input" type="text" v-model="searchValue" class="grow" :placeholder="t('agentStyles.searchPlaceholder', 'Search')" maxlength="200" autocomplete="off" autocapitalize="none" spellcheck="false" />
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="h-4 w-4 opacity-70">
          <path fill-rule="evenodd"
            d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
            clip-rule="evenodd" />
        </svg>
      </label>
      
      <div class="flex items-center space-x-2 rtl:space-x-reverse">
        <button id="create-new-style-button" class="btn" @click="handleCreateClick">{{ t('agentStyles.createNewStyle', 'Create New Style') }}</button>
        <button id="generate-with-ai-button" class="btn flex items-center" @click="handleAIGenerateClick">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-linejoin="round" stroke-width="2" d="M15 19c1.2-3.678 2.526-5.005 6-6c-3.474-.995-4.8-2.322-6-6c-1.2 3.678-2.526 5.005-6 6c3.474.995 4.8 2.322 6 6Zm-8-9c.6-1.84 1.263-2.503 3-3c-1.737-.497-2.4-1.16-3-3c-.6 1.84-1.263 2.503-3 3c1.737.497 2.4 1.16 3 3Zm1.5 10c.3-.92.631-1.251 1.5-1.5c-.869-.249-1.2-.58-1.5-1.5c-.3.92-.631 1.251-1.5 1.5c.869-.249 1.2.58 1.5 1.5Z"/></svg>
          <span class="ml-1 rtl:mr-1 rtl:ml-0">{{ t('agentStyles.generateWithAI', 'Generate With AI') }}</span>
        </button>
      </div>
    </div>
  </div>

  <div class="mt-6 px-4">
    <ThemeOnly theme="modern" :invert="true">
      <div class="overflow-x-auto text-align card shadow bg-white dark:bg-gray-800 w-full rounded-md p-3 border dark:border-gray-700 flex"
        style="border-radius: 10px; height: 70vh;">
        <EasyDataTable 
      :headers="headers" 
      :items="styles" 
      buttons-pagination 
      :search-field="searchFields"
      :search-value="searchValue">
      
      <template #item-logo="item">
        <div class="w-10 h-10 flex items-center justify-center overflow-hidden border rounded bg-gray-50">
          <div v-if="item.logotype === 'code' && isDefinedAndNotEmpty(item.logocode) !== false" class="w-full h-full flex items-center justify-center">
            <object :data="'data:image/svg+xml,' + encodeURIComponent(item.logocode)" type="image/svg+xml" class="w-full h-full" aria-hidden="true" tabindex="-1"></object>
          </div>
          <template v-else-if="isDefinedAndNotEmpty(item.logobase64) !== false">
            <object v-if="typeof item.logobase64 === 'string' && item.logobase64.indexOf('data:image/svg+xml') === 0" :data="item.logobase64" type="image/svg+xml" class="w-full h-full" aria-hidden="true" tabindex="-1"></object>
            <img v-else :src="item.logobase64" class="w-full h-full object-contain" />
          </template>
          <span v-else class="text-xs text-gray-400">No Logo</span>
        </div>
      </template>

      <template #item-cssstyles="item">
        <div class="max-w-xs truncate">
          <span class="text-gray-500">{{ t('agentStyles.cssStylesApplied', 'CSS Styles Applied') }}</span>
        </div>
      </template>
      <template #item-termsmodalcontent="item">
        <div class="max-w-xs truncate">{{ item.termsmodalcontent }}</div>
      </template>
      <template #item-createdat="item">
        {{ new Date(item.createdat).toLocaleDateString() }}
      </template>
      
      <template #item-options="{ styleid }">
        <button @click="handleEditClick(styleid)" class="btn btn-sm mr-2">{{ t('agentStyles.editButton', 'Edit') }}</button>
        <button @click="handleDeleteClick(styleid)" class="btn btn-sm btn-error">{{ t('agentStyles.deleteButton', 'Delete') }}</button>
      </template>

      <template #loading>
        <ModernLoader />
      </template>
    </EasyDataTable>
      </div>
    </ThemeOnly>

    <ThemeOnly theme="modern">
      <ModernAgentTable 
        :headers="headers" 
        :items="styles" 
        :loading="styles.length === 0"
      >
        <template #item-logo="{ item }">
          <div class="w-10 h-10 flex items-center justify-center overflow-hidden border rounded bg-gray-50">
            <div v-if="item.logotype === 'code' && isDefinedAndNotEmpty(item.logocode) !== false" class="w-full h-full flex items-center justify-center">
              <object :data="'data:image/svg+xml,' + encodeURIComponent(item.logocode)" type="image/svg+xml" class="w-full h-full" aria-hidden="true" tabindex="-1"></object>
            </div>
            <template v-else-if="isDefinedAndNotEmpty(item.logobase64) !== false">
              <object v-if="typeof item.logobase64 === 'string' && item.logobase64.indexOf('data:image/svg+xml') === 0" :data="item.logobase64" type="image/svg+xml" class="w-full h-full" aria-hidden="true" tabindex="-1"></object>
              <img v-else :src="item.logobase64" class="w-full h-full object-contain" />
            </template>
            <span v-else class="text-xs text-gray-400">No Logo</span>
          </div>
        </template>

        <template #item-cssstyles="{ item }">
          <div class="max-w-xs truncate">
            <span class="text-gray-500 font-medium">{{ t('agentStyles.cssStylesApplied', 'CSS Styles Applied') }}</span>
          </div>
        </template>
        <template #item-termsmodalcontent="{ item }">
          <div class="max-w-xs truncate text-gray-500 italic">{{ item.termsmodalcontent || '—' }}</div>
        </template>
        <template #item-createdat="{ item }">
          {{ new Date(item.createdat).toLocaleDateString() }}
        </template>
        
        <template #item-options="{ item }">
          <div class="flex items-center justify-end">
            <button @click="handleEditClick(item.styleid)" class="btn btn-sm mr-2">
              <Icon icon="tabler:edit" width="16" />
              <span class="btn-text">{{ t('agentStyles.editButton', 'Edit') }}</span>
            </button>
            <button @click="handleDeleteClick(item.styleid)" class="btn btn-sm btn-error">
              <Icon icon="tabler:trash" width="16" />
              <span class="btn-text">{{ t('agentStyles.deleteButton', 'Delete') }}</span>
            </button>
          </div>
        </template>
      </ModernAgentTable>
    </ThemeOnly>
  </div>

  <div id="agent-style-modal" class="modal" :class="{ 'modal-open': showCreateModal === true }">
    <div class="modal-box agent-style-modal-box w-11/12 max-w-5xl overflow-y-auto max-h-[90vh] relative">
      <div class="agent-style-modal-header sticky top-0 bg-white dark:bg-gray-800 z-10">
        <div class="agent-style-modal-header-row">
          <div class="min-w-0 pr-3">
            <h3 class="font-bold text-lg leading-6 text-gray-900 dark:text-gray-100">
              {{ isEditMode === true ? t('agentStyles.editTitle', 'Edit Style') : t('agentStyles.createTitle', 'Create New Style') }}
            </h3>
            <div class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {{ t('agentStyles.modal.subtitle', 'Configure branding, content, and behavior for this style.') }}
            </div>
          </div>
          <button type="button" class="btn btn-ghost btn-sm btn-circle" @click="closeModal" :aria-label="t('common.close', 'Close')">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>

        <div role="tablist" class="tabs tabs-lifted mt-3">
          <input type="radio" name="styleTabs" role="tab" class="tab" :aria-label="t('agentStyles.tabs.configuration', 'Configuration')" checked />
          <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-5 md:p-6">
            <div class="agent-form">
              <div class="agent-section">
                <div class="agent-section-title">{{ t('agentStyles.sections.basics', 'Basics') }}</div>
                <div class="agent-row">
                  <div>
                    <SonarixInput
                      id="agent-style-name-input"
                      v-model="currentStyle.stylename"
                      :label="t('agentStyles.styleNameLabel', 'Style Name') + ':'"
                      :placeholder="t('agentStyles.styleNamePlaceholder', 'Enter style name')"
                      :required="true"
                      :max-length="100"
                      :pattern="styleNamePatternString"
                      :error="styleNameError === true"
                      :error-message="styleNameErrorMessage"
                      :show-char-count="true"
                      autocomplete="off"
                      input-mode="text"
                      autocapitalize="none"
                      :spellcheck="false"
                      @input="handleStyleNameInput"
                    >
                      <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="h-4 w-4 opacity-70">
                          <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM12.735 14c.618 0 1.093-.561.872-1.139a6.002 6.002 0 0 0-11.215 0c-.22.578.254 1.139.872 1.139h9.47Z" />
                        </svg>
                      </template>
                    </SonarixInput>
                  </div>

                  <div>
                    <label class="label">
                      <span class="label-text">{{ t('agentStyles.styleTypeLabel', 'Style Type') }}</span>
                    </label>
                    <select v-model="currentStyle.styleType" class="select select-bordered w-full">
                      <option value="Introducation Intro">{{ t('agentStyles.styleTypes.intro', 'Introducation Intro') }}</option>
                      <option value="Regular Chat">{{ t('agentStyles.styleTypes.regular', 'Regular Chat') }}</option>
                      <option value="Website + AI">Website + AI</option>
                    </select>
                  </div>
                </div>
              </div>

              <div v-if="currentStyle.styleType === 'Website + AI'" class="agent-section">
                <div class="agent-section-title">{{ t('agentStyles.sections.websiteAi', 'Website + AI') }}</div>
                <div class="agent-form">
                  <div class="agent-row">
                    <div>
                      <label class="label">
                        <span class="label-text">{{ t('agentStyles.websiteAi.title', 'Top Title (supports **bold** markdown)') }}</span>
                      </label>
                      <textarea v-model="currentStyle.websiteAiTitle" class="textarea textarea-bordered w-full h-24" maxlength="60" autocomplete="off" autocapitalize="none" spellcheck="false"></textarea>
                    </div>
                    <div>
                      <label class="label">
                        <span class="label-text">{{ t('agentStyles.websiteAi.subtitle', 'Subtitle Text') }}</span>
                      </label>
                      <textarea v-model="currentStyle.websiteAiSubtitle" class="textarea textarea-bordered w-full h-24" maxlength="60" autocomplete="off" autocapitalize="none" spellcheck="false"></textarea>
                    </div>
                  </div>

                  <div>
                    <label class="label cursor-pointer agent-check">
                      <input type="checkbox" v-model="websiteAiHideMenu" class="toggle toggle-primary" />
                      <span class="label-text agent-check-text">{{ t('agentStyles.websiteAi.hideMenu', 'Hide Website + AI menu and open directly in chat') }}</span>
                    </label>
                  </div>
                  <div>
                    <label class="label cursor-pointer agent-check">
                      <input type="checkbox" v-model="currentStyle.websiteAiShowAlert" class="checkbox checkbox-primary" />
                      <span class="label-text agent-check-text">{{ t('agentStyles.websiteAi.showAlert', 'Show Widget') }}</span>
                    </label>
                  </div>
                  <div v-if="currentStyle.websiteAiShowAlert === true">
                    <label class="label">
                      <span class="label-text">{{ t('agentStyles.websiteAi.alertText', 'Widget Text') }}</span>
                    </label>
                    <textarea v-model="currentStyle.websiteAiAlertText" class="textarea textarea-bordered w-full h-24" maxlength="250" autocomplete="off" autocapitalize="none" spellcheck="false"></textarea>
                  </div>
                  <div>
                    <label class="label">
                      <span class="label-text">{{ t('agentStyles.websiteAi.description', 'Main Disclaimer Content') }}</span>
                    </label>
                    <textarea v-model="currentStyle.websiteAiDescription" class="textarea textarea-bordered w-full h-24" maxlength="600" autocomplete="off" autocapitalize="none" spellcheck="false"></textarea>
                  </div>
                  <div>
                    <label class="label">
                      <span class="label-text">{{ t('agentStyles.websiteAi.newConversationDescription', 'New Conversation Description') }}</span>
                    </label>
                    <textarea v-model="currentStyle.newConversationDescription" class="textarea textarea-bordered w-full h-24" maxlength="600" autocomplete="off" autocapitalize="none" spellcheck="false"></textarea>
                  </div>

                  <div class="agent-row">
                    <div>
                      <label class="label">
                        <span class="label-text">{{ t('agentStyles.websiteAi.floatingButtonText', 'Floating Button Text') }}</span>
                      </label>
                      <textarea v-model="currentStyle.websiteAiFloatingButtonText" class="textarea textarea-bordered w-full h-24" maxlength="40" autocomplete="off" autocapitalize="none" spellcheck="false"></textarea>
                    </div>
                    <div>
                      <label class="label">
                        <span class="label-text">{{ t('agentStyles.websiteAi.floatingButtonSubtitle', 'Floating Button Subtitle') }}</span>
                      </label>
                      <textarea v-model="currentStyle.websiteAiFloatingButtonSubtitle" class="textarea textarea-bordered w-full h-24" maxlength="80" autocomplete="off" autocapitalize="none" spellcheck="false"></textarea>
                    </div>
                  </div>

                  <div class="agent-row">
                    <div>
                      <label class="label">
                        <span class="label-text">{{ t('agentStyles.websiteAi.aiButtonText', 'AI Button Text') }}</span>
                      </label>
                      <textarea v-model="currentStyle.websiteAiAiButtonText" class="textarea textarea-bordered w-full h-24" maxlength="30" autocomplete="off" autocapitalize="none" spellcheck="false"></textarea>
                    </div>
                    <div>
                      <label class="label">
                        <span class="label-text">{{ t('agentStyles.websiteAi.websiteButtonText', 'Website Button Text') }}</span>
                      </label>
                      <textarea v-model="currentStyle.websiteAiWebsiteButtonText" class="textarea textarea-bordered w-full h-24" maxlength="30" autocomplete="off" autocapitalize="none" spellcheck="false"></textarea>
                    </div>
                  </div>

                  <div>
                    <label class="label">
                      <span class="label-text">{{ t('agentStyles.websiteAi.url', 'Website URL') }}</span>
                    </label>
                    <textarea v-model="currentStyle.websiteAiUrl" class="textarea textarea-bordered w-full h-24" inputmode="url" autocomplete="off" autocapitalize="none" spellcheck="false" maxlength="2048"></textarea>
                  </div>
                </div>
              </div>

              <div class="agent-section">
                <div class="agent-section-title">{{ t('agentStyles.sections.branding', 'Branding') }}</div>
                <div class="agent-form">
                  <div class="agent-row">
                    <div>
                      <label class="label" id="primary-color-label">
                        <span class="label-text">{{ t('agentStyles.primaryColorLabel', 'Primary Color') }}</span>
                      </label>
                      <input type="color" v-model="currentStyle.primaryColor" class="w-full h-12 p-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 cursor-pointer" />
                    </div>
                    <div>
                      <label class="label" id="secondary-color-label">
                        <span class="label-text">{{ t('agentStyles.secondaryColorLabel', 'Secondary Color') }}</span>
                      </label>
                      <input type="color" v-model="currentStyle.secondaryColor" class="w-full h-12 p-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 cursor-pointer" />
                    </div>
                  </div>

                  <div class="agent-row">
                    <div>
                      <label class="label">
                        <span class="label-text">{{ t('agentStyles.logoLabel', 'Logo') }}</span>
                      </label>
                      <div class="flex flex-col mb-4">
                        <label class="label">
                          <span class="label-text text-xs text-gray-500">{{ t('agentStyles.logoTypeLabel', 'Logo Type') }}</span>
                        </label>
                        <select v-model="currentStyle.logoType" class="select select-bordered select-sm w-full">
                          <option value="image">{{ t('agentStyles.logoTypes.image', 'Image Upload') }}</option>
                          <option value="code">{{ t('agentStyles.logoTypes.code', 'SVG Code') }}</option>
                        </select>
                      </div>

                      <div v-if="currentStyle.logoType === 'image'" class="flex flex-col items-center">
                          <div
                            class="w-full p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                            :class="{ 'border-primary': isDragging === true, 'border-gray-300': isDragging === false }"
                            @dragenter.prevent="isDragging = true"
                            @dragleave.prevent="isDragging = false"
                            @dragover.prevent
                            @drop.prevent="handleDrop"
                            @click="fileInput.click()"
                          >
                            <div class="flex flex-col items-center justify-center pt-3 pb-4">
                              <svg class="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                              </svg>
                              <p class="mb-1 text-xs text-gray-500 dark:text-gray-400">
                                <span class="font-semibold">{{ t('agentStyles.clickOrDrag', 'Click or drag') }}</span> {{ t('agentStyles.logo', 'logo') }}
                              </p>
                              <p class="text-xs text-gray-500 dark:text-gray-400">{{ t('agentStyles.logoHint', 'SVG, PNG, JPG, GIF (Max 5MB)') }}</p>
                            </div>
                            <input
                              ref="fileInput"
                              type="file"
                              class="hidden"
                              accept="image/svg+xml, image/png, image/jpeg, image/gif"
                              @change="handleLogoUpload"
                            />
                          </div>

                          <div v-if="isDefinedAndNotEmpty(currentStyle.logobase64) !== false" class="mt-3 relative">
                            <object v-if="typeof currentStyle.logobase64 === 'string' && currentStyle.logobase64.indexOf('data:image/svg+xml') === 0" :data="currentStyle.logobase64" type="image/svg+xml" class="w-24 h-24 max-w-[200px] max-h-24 rounded-lg shadow-sm" aria-hidden="true" tabindex="-1"></object>
                            <img v-else :src="currentStyle.logobase64" alt="Logo preview" class="max-w-[200px] max-h-24 object-contain rounded-lg shadow-sm" />
                            <button
                              @click.prevent="removeLogo"
                              class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors duration-200"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        <div v-if="currentStyle.logoType === 'code'" class="flex flex-col">
                          <div class="flex items-center justify-between mb-2">
                            <label class="m-0">
                              <span class="label-text text-xs text-gray-500">{{ t('agentStyles.logoCodeLabel', 'SVG Code') }}</span>
                            </label>
                          </div>

                          <div class="w-full">
                            <LightIDE v-model="currentStyle.logoCode" />
                          </div>

                          <div v-if="isDefinedAndNotEmpty(currentStyle.logoCode) !== false" class="mt-3 relative flex justify-center border rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                            <div class="w-24 h-24 flex items-center justify-center overflow-hidden">
                              <object :data="'data:image/svg+xml,' + encodeURIComponent(currentStyle.logoCode)" type="image/svg+xml" class="w-full h-full" aria-hidden="true" tabindex="-1"></object>
                            </div>
                            <button
                              @click.prevent="currentStyle.logoCode = ''"
                              class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors duration-200"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        </div>
                    </div>

                    <div>
                      <label class="label">
                        <span class="label-text">{{ t('agentStyles.chatIconLabel', 'Chat Icon') }}</span>
                      </label>
                      <div class="flex flex-col items-center">
                        <div
                          class="w-full p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors duration-200 h-[126px]"
                          :class="{ 'border-primary': isDragging === true, 'border-gray-300': isDragging === false }"
                          @dragenter.prevent="isDragging = true"
                          @dragleave.prevent="isDragging = false"
                          @dragover.prevent
                          @drop.prevent="handleChatIconDrop"
                          @click="chatIconInput.click()"
                        >
                          <div class="flex flex-col items-center justify-center pt-3 pb-4">
                            <svg class="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                            </svg>
                            <p class="mb-1 text-xs text-gray-500 dark:text-gray-400">
                              <span class="font-semibold">{{ t('agentStyles.clickOrDrag', 'Click or drag') }}</span> {{ t('agentStyles.icon', 'icon') }}
                            </p>
                            <p class="text-xs text-gray-500 dark:text-gray-400">{{ t('agentStyles.iconHint', 'SVG, PNG, or GIF (Max 1MB)') }}</p>
                          </div>
                          <input
                            ref="chatIconInput"
                            type="file"
                            class="hidden"
                            accept="image/svg+xml, image/png, image/gif"
                            @change="handleChatIconUpload"
                          />
                        </div>

                        <div v-if="isDefinedAndNotEmpty(currentStyle.chatIconBase64) !== false" class="mt-3 relative">
                          <img
                            :src="currentStyle.chatIconBase64"
                            alt="Chat Icon preview"
                            class="max-w-[200px] max-h-20 object-contain rounded-lg shadow-sm"
                          />
                          <button
                            @click.prevent="removeChatIcon"
                            class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors duration-200"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="agent-section">
                <div class="agent-section-title">{{ t('agentStyles.sections.chat', 'Chat') }}</div>
                <div class="agent-form">
                  <div>
                    <label class="label" id="chat-input-placeholder-label">
                      <span class="label-text">{{ t('agentStyles.chatInputPlaceholderLabel', 'Chat Input Placeholder') }}</span>
                    </label>
                    <input type="text" v-model="currentStyle.chatInputPlaceholder" class="input input-bordered w-full" :placeholder="t('agentStyles.chatInputPlaceholderPlaceholder', 'e.g., Type your question here...')" maxlength="120" autocomplete="off" autocapitalize="none" spellcheck="false" />
                  </div>

                  <div>
                    <label class="label cursor-pointer agent-check" id="show-message-under-textbox-label">
                      <input type="checkbox" v-model="currentStyle.showMessageUnderTextbox" class="checkbox checkbox-primary" />
                      <span class="label-text agent-check-text">{{ t('agentStyles.showMessageUnderTextbox', 'Show message under chat input') }}</span>
                    </label>
                  </div>
                  <div v-if="currentStyle.showMessageUnderTextbox === true">
                    <label class="label" id="message-under-textbox-label">
                      <span class="label-text">{{ t('agentStyles.placeholders.messageUnderTextbox', 'Enter message under chat input') }}</span>
                    </label>
                    <input type="text" v-model="currentStyle.messageUnderTextbox" class="input input-bordered w-full" :placeholder="t('agentStyles.placeholders.messageUnderTextbox', 'Enter message under chat input')" maxlength="200" autocomplete="off" autocapitalize="none" spellcheck="false" />
                  </div>
                </div>
              </div>

              <div class="agent-section">
                <div class="agent-section-title">{{ t('agentStyles.sections.terms', 'Terms & Conditions') }}</div>
                <div class="agent-form">
                  <div>
                    <label class="label cursor-pointer agent-check" id="show-terms-modal-label">
                      <input type="checkbox" v-model="currentStyle.showTermsModal" class="checkbox checkbox-primary" />
                      <span class="label-text agent-check-text">{{ t('agentStyles.showTermsModal', 'Show terms modal before chat') }}</span>
                    </label>
                  </div>
                  <div v-if="currentStyle.showTermsModal === true">
                    <label class="label" id="terms-modal-content-label">
                      <span class="label-text">{{ t('agentStyles.placeholders.termsModalContent', 'Enter terms and conditions content') }}</span>
                    </label>
                    <MarkdownWysiwyg v-model="currentStyle.termsModalContent" :placeholder="t('agentStyles.placeholders.termsModalContent', 'Enter terms and conditions content')" />
                  </div>
                  <div v-if="currentStyle.showTermsModal === true">
                    <label class="label" id="terms-button-text-label">
                      <span class="label-text">{{ t('agentStyles.termsButtonTextLabel', 'Terms Button Text') }}</span>
                    </label>
                    <input type="text" v-model="currentStyle.termsButtonText" class="input input-bordered w-full" :placeholder="t('agentStyles.placeholders.termsButtonText', 'Enter terms button text')" maxlength="40" autocomplete="off" autocapitalize="none" spellcheck="false" />
                  </div>
                </div>
              </div>

              <div class="agent-section">
                <div class="agent-section-title">{{ t('agentStyles.sections.legal', 'Legal') }}</div>
                <div class="agent-form">
                  <div>
                    <label class="label">
                      <span class="label-text">{{ t('agentStyles.legalDisclaimer.title', 'Legal Disclaimer') }}</span>
                    </label>
                    <textarea v-model="currentStyle.legalDisclaimer" class="textarea textarea-bordered w-full h-24" maxlength="600" autocomplete="off" autocapitalize="none" spellcheck="false"></textarea>
                  </div>
                  <div class="agent-row">
                    <div>
                      <label class="label">
                        <span class="label-text">{{ t('agentStyles.legalDisclaimer.label', 'Legal Disclaimer Link Label') }}</span>
                      </label>
                      <input type="text" v-model="currentStyle.legalDisclaimerLabel" class="input input-bordered w-full" maxlength="60" autocomplete="off" autocapitalize="none" spellcheck="false" />
                    </div>
                    <div>
                      <label class="label">
                        <span class="label-text">{{ t('agentStyles.legalDisclaimer.url', 'Legal Disclaimer URL') }}</span>
                      </label>
                      <input type="url" v-model="currentStyle.legalDisclaimerUrl" class="input input-bordered w-full" inputmode="url" autocomplete="off" autocapitalize="none" spellcheck="false" maxlength="2048" />
                    </div>
                  </div>
                </div>
              </div>

              <div class="agent-section" id="generate-from-screenshot-section">
                <div class="agent-section-title">{{ t('agentStyles.generateFromScreenshot.title', 'Generate from Screenshot') }}</div>
                <div class="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {{ t('agentStyles.generateFromScreenshot.description', "Upload a screenshot of your website or your brand logo, and we'll automatically extract colors and suggest CSS for you.") }}
                </div>
                <input type="file" @change="handleAIDesignUpload" accept="image/png, image/jpeg, image/gif" class="file-input file-input-bordered w-full" :disabled="isGeneratingStyle === true" />
                <div v-if="isGeneratingStyle !== false" class="mt-4 flex items-center">
                  <ModernLoader />
                  <span class="ml-2">{{ t('agentStyles.generateFromScreenshot.loading', 'Analyzing image and generating styles...') }}</span>
                </div>
              </div>
            </div>
          </div>

          <input type="radio" name="styleTabs" role="tab" class="tab" :aria-label="t('agentStyles.tabs.css', 'CSS')" />
          <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
   
  <div class="flex items-center justify-between mb-4">
    <label class="m-0">
      <span class="label-text">{{ t('agentStyles.cssStylesLabel', 'CSS Styles') }}</span>
    </label>
    <div class="flex items-center">
      <input type="checkbox" id="light-ide-toggle" class="toggle toggle-primary" v-model="isLightIDE" />
      <label for="light-ide-toggle" class="ml-2 m-0">{{ t('agentStyles.useLightIDE', 'Use LightIDE') }}</label>
    </div>
  </div>
  <div v-if="isLightIDE === true">
                <LightIDE v-model="currentStyle.cssstyles" />
              </div>
              <div v-else class="w-full" ref="editorContainer">
                <div 
                  ref="monacoContainer" 
                  class="monaco-editor-container border border-gray-300 dark:border-gray-700 rounded-[10px] overflow-hidden" 
                  style="height: 400px; width: 100%;" 
                ></div>
              </div>
            </div>
          
          <input type="radio" name="styleTabs" role="tab" class="tab" :aria-label="t('agentStyles.tabs.clickToAction', 'Click To Action')" />
          <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
            <div class="agent-form">
              <div class="flex items-start rounded-lg border border-base-300 p-4 hover:bg-base-200/40 transition-colors">
                <input type="checkbox" v-model="currentStyle.addShadows" class="checkbox checkbox-primary mt-1 mr-3" aria-describedby="cta-desc-shadows" />
                <div>
                  <div class="font-medium">{{ t('agentStyles.clickToAction.addShadows', 'Add shadows') }}</div>
                  <div id="cta-desc-shadows" class="text-sm text-gray-500 mt-1">{{ t('agentStyles.clickToAction.descriptions.addShadows', 'Adds subtle shadows to chat elements to improve depth and visibility.') }}</div>
                </div>
              </div>
              <div class="flex items-start rounded-lg border border-base-300 p-4 hover:bg-base-200/40 transition-colors">
                <input type="checkbox" v-model="currentStyle.addPulse" class="checkbox checkbox-primary mt-1 mr-3" aria-describedby="cta-desc-pulse" />
                <div>
                  <div class="font-medium">{{ t('agentStyles.clickToAction.addPulse', 'Add pulse') }}</div>
                  <div id="cta-desc-pulse" class="text-sm text-gray-500 mt-1">{{ t('agentStyles.clickToAction.descriptions.addPulse', 'Adds a gentle pulsing animation to the chat button to draw attention without being intrusive.') }}</div>
                </div>
              </div>
              <div class="flex items-start rounded-lg border border-base-300 p-4 hover:bg-base-200/40 transition-colors">
                <input type="checkbox" v-model="currentStyle.showFloatingMessage" class="checkbox checkbox-primary mt-1 mr-3" aria-describedby="cta-desc-floating" />
                <div class="w-full">
                  <div class="font-medium">{{ t('agentStyles.clickToAction.showFloatingMessage', 'Show floating message') }}</div>
                  <div id="cta-desc-floating" class="text-sm text-gray-500 mt-1">{{ t('agentStyles.clickToAction.descriptions.showFloatingMessage', 'Displays a small floating tooltip near the chat button with a short call-to-action.') }}</div>
                  <div v-if="currentStyle.showFloatingMessage === true" class="mt-3">
                    <label class="label">
                      <span class="label-text">{{ t('agentStyles.clickToAction.floatingMessageLabel', 'Floating Message') }}</span>
                    </label>
                    <textarea v-model="currentStyle.floatingMessageText" class="textarea textarea-bordered w-full h-32" :placeholder="t('agentStyles.placeholders.floatingMessageText', 'Enter floating message text')"></textarea>
                    <div class="text-xs text-gray-500 mt-2">{{ t('agentStyles.clickToAction.descriptions.floatingMessageText', 'Short message shown above the chat button, for example: "Need help? Ask me!"') }}</div>
                  </div>
                </div>
              </div>
              <div class="flex items-start rounded-lg border border-base-300 p-4 hover:bg-base-200/40 transition-colors">
                <input type="checkbox" v-model="currentStyle.answerAnimationEnabled" class="checkbox checkbox-primary mt-1 mr-3" aria-describedby="cta-desc-answer-animation" />
                <div class="w-full">
                  <div class="font-medium">{{ t('agentStyles.clickToAction.answerAnimation', 'Animate answer progress text') }}</div>
                  <div id="cta-desc-answer-animation" class="text-sm text-gray-500 mt-1">{{ t('agentStyles.clickToAction.descriptions.answerAnimation', 'Rotates the selected language sentences while the agent prepares an answer.') }}</div>
                  <div v-if="currentStyle.answerAnimationEnabled === true" class="mt-3">
                    <div class="flex items-end">
                      <label class="form-control w-1/2">
                        <span class="label-text">{{ t('agentStyles.clickToAction.answerAnimationLanguage', 'Language') }}</span>
                        <select v-model="answerAnimationLanguage" class="select select-bordered w-full">
                          <option value="he">{{ t('languages.he', 'Hebrew') }}</option>
                          <option value="en">{{ t('languages.en', 'English') }}</option>
                          <option value="ar">{{ t('languages.ar', 'Arabic') }}</option>
                        </select>
                      </label>
                      <label class="form-control w-1/2 ml-3">
                        <span class="label-text">{{ t('agentStyles.clickToAction.answerAnimationInterval', 'Switch every (milliseconds)') }}</span>
                        <input v-model.number="currentStyle.answerAnimationIntervalMs" class="input input-bordered w-full" type="number" min="500" max="10000" step="100" />
                      </label>
                    </div>
                    <div v-for="(sentence, sentenceIndex) in currentAnswerAnimationSentences" :key="answerAnimationLanguage + '-' + String(sentenceIndex)" class="flex items-center mt-3">
                      <input :value="sentence" class="input input-bordered flex-1" type="text" maxlength="140" @input="updateAnswerAnimationSentence(sentenceIndex, $event.target.value)" />
                      <button type="button" class="btn btn-error btn-sm ml-3" @click="removeAnswerAnimationSentence(sentenceIndex)">{{ t('common.delete', 'Delete') }}</button>
                    </div>
                    <button type="button" class="btn btn-sm mt-3" @click="addAnswerAnimationSentence">{{ t('agentStyles.clickToAction.addAnswerAnimationSentence', 'Add sentence') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      

      <div class="agent-style-modal-footer sticky bottom-0 bg-white dark:bg-gray-800 modal-action z-10">
        <button class="btn" @click="closeModal">{{ t('agentStyles.cancelButton', 'Cancel') }}</button>
        <button class="btn" @click="saveStyle">{{ t('agentStyles.saveButton', 'Save') }}</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="closeModal">close</button>
    </form>
  </div>

  <div class="modal" :class="{ 'modal-open': isDeleteModalOpen === true }">
    <div class="modal-box">
      <h3 class="font-bold text-lg">{{ t('agentStyles.deleteModal.title', 'Are you sure?') }}</h3>
      <p class="py-4">{{ t('agentStyles.deleteModal.text', 'Are you sure you want to delete this style?') }}</p>
      <div class="modal-action">
        <button @click="isDeleteModalOpen = false" class="btn">{{ t('agentStyles.deleteModal.cancelButton', 'Cancel') }}</button>
        <button @click="deleteStyleConfirmed()" class="btn btn-error">{{ t('agentStyles.deleteModal.deleteButton', 'Delete') }}</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="isDeleteModalOpen = false">close</button>
    </form>
  </div>

  <div class="modal" :class="{ 'modal-open': showAIGenerateModal === true }">
    <div class="modal-box w-11/12 max-w-lg">
      <h3 class="font-bold text-lg mb-4">{{ t('agentStyles.aiGenerateModal.title', 'Generate Styles With AI') }}</h3>
      <p class="mb-4">{{ t('agentStyles.aiGenerateModal.description', 'Upload a screenshot of your website or brand logo and let AI suggest colors and CSS.') }}</p>
      <input type="file" @change="handleAIDesignUpload" accept="image/png, image/jpeg, image/gif" class="file-input file-input-bordered w-full" :disabled="isGeneratingStyle === true" />
      <div v-if="isGeneratingStyle !== false" class="mt-4 flex items-center">
        <ModernLoader />
        <span class="ml-2">{{ t('agentStyles.generateFromScreenshot.loading', 'Analyzing image and generating styles...') }}</span>
      </div>
      <div class="modal-action mt-4">
        <button class="btn" @click="showAIGenerateModal = false">{{ t('agentStyles.aiGenerateModal.closeButton', 'Close') }}</button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="showAIGenerateModal = false">close</button>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch, onUnmounted, computed } from 'vue';
import axios from '../api/axiosInstance';
import { globalState } from '@/globalState';
import { useToast } from 'vue-toastification';
import EasyDataTable from "vue3-easy-data-table";
import loader from '@monaco-editor/loader';
import hljs from 'highlight.js';
import Vue3EasyDataTable from 'vue3-easy-data-table';
import 'vue3-easy-data-table/dist/style.css';
import { useI18n } from '@/i18n';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
import LightIDE from '../components/LightIDE.vue';
import ModernLoader from '@/components/ModernLoader.vue';
import MarkdownWysiwyg from '@/components/MarkdownWysiwyg.vue';
import SonarixInput from '@/components/SonarixInput.vue';
import ThemeOnly from '@/components/ThemeOnly.vue';
import ModernAgentTable from '@/components/ModernAgentTable.vue';
import { Icon } from '@iconify/vue';

const components = {
  EasyDataTable: Vue3EasyDataTable
};

const apiEndpoint = globalState.apiEndpoint;
const styles = ref([]);
const showCreateModal = ref(false);
const isEditMode = ref(false);
const searchFields = ["styleid", "stylename", "cssstyles", "styletype"];
const searchValue = ref("");
const editorContainer = ref(null);
const monacoContainer = ref(null);
let monacoEditorInstance = null;
let resizeTimeout = null;
const isDeleteModalOpen = ref(false);
const selectedStyleId = ref(null);
const isDragging = ref(false);
const fileInput = ref(null);
const chatIconInput = ref(null);
const isGeneratingStyle = ref(false);
const showAIGenerateModal = ref(false);
const isLightIDE = ref(false);
const termsEditor = ref(null);
const showTermsPreview = ref(false);
const styleNameError = ref(false);
const styleNameErrorMessage = ref('');

const { t } = useI18n();

const styleNamePatternString = '^[a-zA-Z0-9 \\u0590-\\u05FF_.-]{1,100}$';

const safeTrim = (value) => {
  if (typeof value === 'string') {
    return value.trim();
  }
  return '';
};

const logDevError = (message, error) => {
  if (import.meta !== undefined && import.meta.env !== undefined && import.meta.env.DEV === true) {
    console.error(message, error);
  }
};

const validateStyleName = (value) => {
  const name = safeTrim(value);
  if (name.length === 0) {
    return t('agentStyles.toasts.nameRequired', 'Style name is required');
  }
  if (name.length > 100) {
    return t('agentStyles.validation.nameTooLong', 'Style name must be 100 characters or less');
  }
  const re = /^[a-zA-Z0-9 \u0590-\u05FF_.-]{1,100}$/;
  if (re.test(name) === false) {
    return t('agentStyles.validation.nameInvalid', 'Style name can include letters, numbers, spaces, periods, hyphens, and underscores.');
  }
  return '';
};

const isValidHttpUrl = (value) => {
  const v = safeTrim(value);
  if (v.length === 0) {
    return false;
  }
  if (v.length > 2048) {
    return false;
  }
  try {
    const u = new URL(v);
    if (u.protocol === 'http:' || u.protocol === 'https:') {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

const handleStyleNameInput = () => {
  styleNameError.value = false;
  styleNameErrorMessage.value = '';
};

const headers = computed(() => [
  { text: t('agentStyles.headers.styleId', "Style ID"), value: "styleid", sortable: true },
  { text: t('agentStyles.headers.styleName', "Style Name"), value: "stylename", sortable: true },
  { text: t('agentStyles.headers.logo', "Logo"), value: "logo", sortable: false },
  { text: t('agentStyles.headers.styleType', "Style Type"), value: "styletype", sortable: true },
  { text: t('agentStyles.headers.cssStyles', "CSS Styles"), value: "cssstyles", sortable: true },
  { text: t('agentStyles.headers.termsModalContent', "Terms & Conditions"), value: "termsmodalcontent", sortable: false },
  { text: t('agentStyles.headers.createdAt', "Created At"), value: "createdat", sortable: true },
  { text: t('agentStyles.headers.options', "Options"), value: "options" }
]);

const toast = useToast();
const answerAnimationLanguage = ref('he');
const defaultAnswerAnimationTexts = {
  he: ['יוצרת תשובה..', 'מוודא תקינות..', 'בודקת עם מקורות..'],
  en: [],
  ar: []
};

function createDefaultAnswerAnimationTexts() {
  return {
    he: defaultAnswerAnimationTexts.he.slice(),
    en: [],
    ar: []
  };
}

function normalizeAnswerAnimationTexts(value) {
  const normalized = createDefaultAnswerAnimationTexts();
  if (value === undefined || value === null || typeof value !== 'object' || Array.isArray(value) === true) {
    return normalized;
  }
  const languages = ['he', 'en', 'ar'];
  for (let languageIndex = 0; languageIndex < languages.length; languageIndex = languageIndex + 1) {
    const language = languages[languageIndex];
    if (Array.isArray(value[language]) === false) {
      continue;
    }
    const sentences = [];
    for (let sentenceIndex = 0; sentenceIndex < value[language].length && sentenceIndex < 10; sentenceIndex = sentenceIndex + 1) {
      const sentence = typeof value[language][sentenceIndex] === 'string' ? value[language][sentenceIndex].trim() : '';
      if (sentence.length > 0) {
        sentences.push(sentence);
      }
    }
    normalized[language] = sentences;
  }
  return normalized;
}

const currentAnswerAnimationSentences = computed(() => {
  const texts = currentStyle.value.answerAnimationTexts;
  if (texts === undefined || texts === null || Array.isArray(texts) === true || typeof texts !== 'object') {
    return [];
  }
  if (Array.isArray(texts[answerAnimationLanguage.value]) === false) {
    texts[answerAnimationLanguage.value] = [];
  }
  return texts[answerAnimationLanguage.value];
});

function updateAnswerAnimationSentence(sentenceIndex, value) {
  if (sentenceIndex < 0 || sentenceIndex >= currentAnswerAnimationSentences.value.length) {
    return;
  }
  currentAnswerAnimationSentences.value[sentenceIndex] = typeof value === 'string' ? value : '';
}

function addAnswerAnimationSentence() {
  if (currentAnswerAnimationSentences.value.length >= 10) {
    return;
  }
  currentAnswerAnimationSentences.value.push('');
}

function removeAnswerAnimationSentence(sentenceIndex) {
  if (sentenceIndex < 0 || sentenceIndex >= currentAnswerAnimationSentences.value.length) {
    return;
  }
  currentAnswerAnimationSentences.value.splice(sentenceIndex, 1);
}

const currentStyle = ref({
  stylename: '',
  cssstyles: '',
  logobase64: '',
  logoType: 'image',
  logoCode: '',
  primaryColor: '#000000',
  secondaryColor: '#ffffff',
  chatIconBase64: '',
  chatInputPlaceholder: '',
  showMessageUnderTextbox: false,
  messageUnderTextbox: '',
  showTermsModal: false,
  termsModalContent: '',
  termsButtonText: '',
  addShadows: false,
  addPulse: false,
  showFloatingMessage: false,
  floatingMessageText: '',
  answerAnimationEnabled: false,
  answerAnimationTexts: createDefaultAnswerAnimationTexts(),
  answerAnimationIntervalMs: 2600,
  styleType: 'Regular Chat',
  websiteAiFloatingButtonText: '',
  websiteAiFloatingButtonSubtitle: '',
  websiteAiTitle: '',
  websiteAiSubtitle: '',
  websiteAiDescription: '',
  websiteAiShowMenu: true,
  websiteAiShowAlert: false,
  websiteAiAlertText: '',
  newConversationDescription: '',
  websiteAiAiButtonText: '',
  websiteAiWebsiteButtonText: '',
  websiteAiUrl: '',
  legalDisclaimer: '',
  legalDisclaimerUrl: '',
  legalDisclaimerLabel: ''
});

const websiteAiHideMenu = computed({
  get: () => currentStyle.value.websiteAiShowMenu === false,
  set: (hideMenu) => {
    currentStyle.value.websiteAiShowMenu = hideMenu !== true;
  }
});

const formatTerms = (action) => {
  const textarea = termsEditor.value;
  if (textarea === undefined || textarea === null) {
    return;
  }
  const content = currentStyle.value.termsModalContent !== undefined ? currentStyle.value.termsModalContent : '';
  const selStart = textarea.selectionStart;
  const selEnd = textarea.selectionEnd;
  const hasSelection = selEnd > selStart;
  const selectedText = content.substring(selStart, selEnd);
  const before = content.substring(0, selStart);
  const after = content.substring(selEnd);

  if (action === 'bold' || action === 'italic' || action === 'link' || action === 'code' || action === 'codeblock') {
    let replacement = selectedText;
  if (action === 'bold') {
      const text = selectedText.length > 0 ? selectedText : t('agentStyles.editor.boldSample', 'bold text');
      replacement = `**${text}**`;
  }
  if (action === 'italic') {
      const text = selectedText.length > 0 ? selectedText : t('agentStyles.editor.italicSample', 'italic text');
      replacement = `*${text}*`;
  }
  if (action === 'link') {
      const linkText = selectedText.length > 0 ? selectedText : t('agentStyles.editor.linkText', 'link text');
      replacement = `[${linkText}](https://)`;
    }
    if (action === 'code') {
      const text = selectedText.length > 0 ? selectedText : t('agentStyles.editor.codeSample', 'code');
      replacement = `\`${text}\``;
    }
    if (action === 'codeblock') {
      const text = selectedText.length > 0 ? selectedText : t('agentStyles.editor.codeblockSample', 'code block');
      replacement = `\n\n\
 \`\`\`\n${text}\n\
 \`\`\`\n\n`;
    }
    const updated = before + replacement + after;
    currentStyle.value.termsModalContent = updated;
    const newPos = before.length + replacement.length;
    nextTick(() => {
      if (termsEditor.value !== undefined && termsEditor.value !== null) {
        termsEditor.value.focus();
        termsEditor.value.selectionStart = newPos;
        termsEditor.value.selectionEnd = newPos;
      }
    });
    return;
  }

  if (action === 'hr') {
    const insertion = `\n\n---\n\n`;
    const updated = before + insertion + after;
    currentStyle.value.termsModalContent = updated;
    const newPos = before.length + insertion.length;
    nextTick(() => {
      if (termsEditor.value !== undefined && termsEditor.value !== null) {
        termsEditor.value.focus();
        termsEditor.value.selectionStart = newPos;
        termsEditor.value.selectionEnd = newPos;
      }
    });
    return;
  }

  let blockStart = selStart;
  let blockEnd = selEnd;
  while (blockStart > 0 && content.charAt(blockStart - 1) !== '\n') {
    blockStart = blockStart - 1;
  }
  while (blockEnd < content.length && content.charAt(blockEnd) !== '\n') {
    blockEnd = blockEnd + 1;
  }
  const blockText = content.substring(blockStart, blockEnd);
  const blockLines = blockText.split('\n');
  let resultLines = [];
  if (action === 'h1' || action === 'h2' || action === 'h3') {
    const hashes = action === 'h1' ? '# ' : (action === 'h2' ? '## ' : '### ');
    for (let i = 0; i < blockLines.length; i = i + 1) {
      const line = blockLines[i];
      const trimmed = line.trim();
      if (trimmed.length === 0) {
        resultLines.push(line);
      } else {
        resultLines.push(hashes + trimmed);
      }
    }
  } else if (action === 'ul') {
    for (let i = 0; i < blockLines.length; i = i + 1) {
      const line = blockLines[i];
      const trimmed = line.trim();
      if (trimmed.length === 0) {
        resultLines.push(line);
      } else {
        resultLines.push('- ' + trimmed);
      }
    }
  } else if (action === 'ol') {
    let counter = 1;
    for (let i = 0; i < blockLines.length; i = i + 1) {
      const line = blockLines[i];
      const trimmed = line.trim();
      if (trimmed.length === 0) {
        resultLines.push(line);
      } else {
        resultLines.push(String(counter) + '. ' + trimmed);
        counter = counter + 1;
      }
    }
  } else if (action === 'quote') {
    for (let i = 0; i < blockLines.length; i = i + 1) {
      const line = blockLines[i];
      const trimmed = line.trim();
      if (trimmed.length === 0) {
        resultLines.push(line);
      } else {
        resultLines.push('> ' + trimmed);
      }
    }
  }
  if (resultLines.length > 0) {
    const replacement = resultLines.join('\n');
    const updated = content.substring(0, blockStart) + replacement + content.substring(blockEnd);
    currentStyle.value.termsModalContent = updated;
    const newSelStart = blockStart;
    const newSelEnd = blockStart + replacement.length;
    nextTick(() => {
      if (termsEditor.value !== undefined && termsEditor.value !== null) {
        termsEditor.value.focus();
        termsEditor.value.selectionStart = newSelStart;
        termsEditor.value.selectionEnd = newSelEnd;
      }
    });
    return;
  }
};

const getFileExtension = (fileName) => {
  if (typeof fileName !== 'string') {
    return '';
  }
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex < 0) {
    return '';
  }
  if (lastDotIndex === fileName.length - 1) {
    return '';
  }
  return fileName.substring(lastDotIndex + 1).toLowerCase();
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(typeof reader.result === 'string' ? reader.result : '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const getGenerateFromImageErrorMessage = (error) => {
  const responseData = error !== undefined && error !== null && error.response !== undefined && error.response !== null ? error.response.data : null;
  if (responseData !== undefined && responseData !== null) {
    if (typeof responseData.message === 'string' && responseData.message.length > 0) {
      return responseData.message;
    }
    if (typeof responseData.error === 'string' && responseData.error.length > 0) {
      return responseData.error;
    }
  }
  return t('agentStyles.toasts.generateError', 'Failed to generate styles from image.');
};

const loadStyles = async () => {
  try {
    const response = await axios.get(`${apiEndpoint}/api/styles/getAllAgentStyles`);
    styles.value = response.data;
  } catch (error) {
    logDevError('Error loading styles:', error);
    toast.error(t('agentStyles.toasts.loadError', 'Failed to load styles'));
  }
};

const resetForm = () => {
  currentStyle.value = {
    stylename: '',
    cssstyles: 'body {\n  \n}',
    logobase64: '',
    logoType: 'image',
    logoCode: '',
    primaryColor: '#000000',
    secondaryColor: '#ffffff',
    chatIconBase64: '',
    chatInputPlaceholder: '',
    showMessageUnderTextbox: false,
    messageUnderTextbox: '',
    showTermsModal: false,
    termsModalContent: '',
    termsButtonText: '',
    addShadows: false,
    addPulse: false,
    showFloatingMessage: false,
    floatingMessageText: '',
    answerAnimationEnabled: false,
    answerAnimationTexts: createDefaultAnswerAnimationTexts(),
    answerAnimationIntervalMs: 2600,
    styleType: 'Regular Chat',
    websiteAiFloatingButtonText: '',
    websiteAiFloatingButtonSubtitle: '',
    websiteAiTitle: '',
    websiteAiSubtitle: '',
    websiteAiDescription: '',
    websiteAiShowMenu: true,
    websiteAiShowAlert: false,
    websiteAiAlertText: '',
    newConversationDescription: '',
    websiteAiAiButtonText: '',
    websiteAiWebsiteButtonText: '',
    websiteAiUrl: '',
    legalDisclaimer: '',
    legalDisclaimerUrl: '',
    legalDisclaimerLabel: ''
  };
  if (monacoEditorInstance !== null) {
    monacoEditorInstance.setValue('body {\n  \n}');
  }
  isEditMode.value = false;
  styleNameError.value = false;
  styleNameErrorMessage.value = '';
};

const handleDrop = (e) => {
  isDragging.value = false;
  const file = e !== undefined && e !== null && e.dataTransfer !== undefined && e.dataTransfer.files !== undefined ? e.dataTransfer.files[0] : undefined;
  if (file !== undefined && file !== null) {
    handleFile(file);
  }
};

const handleLogoUpload = (event) => {
  const file = event !== undefined && event !== null && event.target !== undefined && event.target.files !== undefined ? event.target.files[0] : undefined;
  if (file !== undefined && file !== null) {
    handleFile(file);
  }
};

const handleFile = (file) => {
  const mimeType = typeof file.type === 'string' ? file.type.toLowerCase() : '';
  const fileExtension = getFileExtension(file.name);
  const allowedExtensions = ['svg', 'png', 'jpeg', 'jpg', 'gif'];
  const mimeAllowed = mimeType.length > 0 && mimeType.startsWith('image/');
  const extensionAllowed = allowedExtensions.includes(fileExtension);
  if (mimeAllowed === false && extensionAllowed === false) {
    toast.error(t('agentStyles.toasts.imageFileRequired', 'Please upload an image file'));
    return;
  }

  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    toast.error(t('agentStyles.toasts.imageSizeError', 'File size should not exceed 5MB'));
    return;
  }

  const reader = new FileReader();
  reader.onloadend = () => {
    currentStyle.value.logobase64 = typeof reader.result === 'string' ? reader.result : '';
  };
  reader.readAsDataURL(file);
};

const removeLogo = () => {
  currentStyle.value.logobase64 = '';
  if (fileInput.value !== undefined && fileInput.value !== null) {
    fileInput.value.value = '';
  }
};

const handleChatIconUpload = (event) => {
  const file = event !== undefined && event !== null && event.target !== undefined && event.target.files !== undefined ? event.target.files[0] : undefined;
  if (file !== undefined && file !== null) {
    handleChatIconFile(file);
  }
};

const handleChatIconDrop = (e) => {
  const file = e !== undefined && e !== null && e.dataTransfer !== undefined && e.dataTransfer.files !== undefined ? e.dataTransfer.files[0] : undefined;
  if (file !== undefined && file !== null) {
    handleChatIconFile(file);
  }
};

const handleChatIconFile = (file) => {
  const mimeType = typeof file.type === 'string' ? file.type.toLowerCase() : '';
  const fileExtension = getFileExtension(file.name);
  const allowedMimes = ['image/svg+xml', 'image/png', 'image/gif'];
  const allowedExtensions = ['svg', 'png', 'gif'];
  const mimeAllowed = mimeType.length > 0 && allowedMimes.includes(mimeType) === true;
  const extensionAllowed = allowedExtensions.includes(fileExtension);
  if (mimeAllowed === false && extensionAllowed === false) {
    toast.error(t('agentStyles.toasts.iconFileRequired', 'Please upload an SVG, PNG, or GIF file for the icon'));
    return;
  }

  const maxSize = 1 * 1024 * 1024;
  if (file.size > maxSize) {
    toast.error(t('agentStyles.toasts.iconSizeError', 'Icon file size should not exceed 1MB'));
    return;
  }

  const reader = new FileReader();
  reader.onloadend = () => {
    currentStyle.value.chatIconBase64 = typeof reader.result === 'string' ? reader.result : '';
  };
  reader.readAsDataURL(file);
};

const removeChatIcon = () => {
  currentStyle.value.chatIconBase64 = '';
  if (chatIconInput.value !== undefined && chatIconInput.value !== null) {
    chatIconInput.value.value = '';
  }
};

const handleAIDesignUpload = async (event) => {
  const file = event !== undefined && event !== null && event.target !== undefined && event.target !== null && event.target.files !== undefined ? event.target.files[0] : null;
  if (file === null) {
    return;
  }

  if (typeof file.type !== 'string' || file.type.startsWith('image/') === false) {
    toast.error(t('agentStyles.toasts.imageFileRequired', 'Please upload an image file.'));
    if (event.target !== undefined && event.target !== null) {
      event.target.value = '';
    }
    return;
  }

  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    toast.error(t('agentStyles.toasts.imageSizeError', 'Image size should not exceed 5MB.'));
    if (event.target !== undefined && event.target !== null) {
      event.target.value = '';
    }
    return;
  }

  const openedFromAiModal = showAIGenerateModal.value === true;
  isGeneratingStyle.value = true;
  try {
    const imageBase64 = await fileToBase64(file);
    if (imageBase64.length === 0) {
      toast.error(t('agentStyles.toasts.imageFileRequired', 'Please upload an image file.'));
      return;
    }
    const response = await axios.post(`${apiEndpoint}/api/styles/generateFromImage`, { imageBase64 });
    const primaryColor = typeof response.data.primaryColor === 'string' ? response.data.primaryColor : currentStyle.value.primaryColor;
    const secondaryColor = typeof response.data.secondaryColor === 'string' ? response.data.secondaryColor : currentStyle.value.secondaryColor;
    const cssStyles = typeof response.data.cssStyles === 'string' ? response.data.cssStyles : '';
    currentStyle.value.primaryColor = primaryColor;
    currentStyle.value.secondaryColor = secondaryColor;
    if (cssStyles.length > 0) {
      const existingCss = typeof currentStyle.value.cssstyles === 'string' ? currentStyle.value.cssstyles : '';
      currentStyle.value.cssstyles = existingCss.length > 0 ? `${existingCss}\n\n${cssStyles}` : cssStyles;
    }
    if (monacoEditorInstance !== null) {
      monacoEditorInstance.setValue(currentStyle.value.cssstyles);
    }
    toast.success(t('agentStyles.toasts.generateSuccess', 'Styles generated successfully from image!'));
    if (openedFromAiModal === true) {
      showAIGenerateModal.value = false;
      isEditMode.value = false;
      showCreateModal.value = true;
    }
  } catch (error) {
    logDevError('Error generating styles from image:', error);
    toast.error(getGenerateFromImageErrorMessage(error));
  } finally {
    isGeneratingStyle.value = false;
    if (event.target !== undefined && event.target !== null) {
      event.target.value = '';
    }
  }
};

const saveStyle = async () => {
  try {
    const nameError = validateStyleName(currentStyle.value.stylename);
    if (nameError.length > 0) {
      styleNameError.value = true;
      styleNameErrorMessage.value = nameError;
      toast.error(nameError);
      return;
    }

    const css = safeTrim(currentStyle.value.cssstyles);
    if (css.length === 0) {
      toast.error(t('agentStyles.toasts.cssRequired', 'CSS styles are required'));
      return;
    }
    if (css.length > 50000) {
      toast.error(t('agentStyles.validation.cssTooLong', 'CSS styles are too long'));
      return;
    }
    const websiteAiFloatingButtonText = safeTrim(currentStyle.value.websiteAiFloatingButtonText);
    if (websiteAiFloatingButtonText.length > 40) {
      toast.error(t('agentStyles.validation.floatingButtonTextTooLong', 'Floating button text is too long'));
      return;
    }
    const websiteAiFloatingButtonSubtitle = safeTrim(currentStyle.value.websiteAiFloatingButtonSubtitle);
    if (websiteAiFloatingButtonSubtitle.length > 80) {
      toast.error(t('agentStyles.validation.floatingButtonSubtitleTooLong', 'Floating button subtitle is too long'));
      return;
    }
    const websiteAiTitle = safeTrim(currentStyle.value.websiteAiTitle);
    if (websiteAiTitle.length > 60) {
      toast.error(t('agentStyles.validation.websiteAiTitleTooLong', 'Panel title is too long'));
      return;
    }
    const websiteAiSubtitle = safeTrim(currentStyle.value.websiteAiSubtitle);
    if (websiteAiSubtitle.length > 60) {
      toast.error(t('agentStyles.validation.websiteAiSubtitleTooLong', 'Panel subtitle is too long'));
      return;
    }
    const websiteAiDescription = safeTrim(currentStyle.value.websiteAiDescription);
    if (websiteAiDescription.length > 600) {
      toast.error(t('agentStyles.validation.websiteAiDescriptionTooLong', 'Panel description is too long'));
      return;
    }
    const websiteAiAlertText = safeTrim(currentStyle.value.websiteAiAlertText);
    if (websiteAiAlertText.length > 250) {
      toast.error(t('agentStyles.validation.websiteAiAlertTextTooLong', 'Website + AI alert text is too long'));
      return;
    }
    const newConversationDescription = safeTrim(currentStyle.value.newConversationDescription);
    if (newConversationDescription.length > 600) {
      toast.error(t('agentStyles.validation.newConversationDescriptionTooLong', 'New conversation description is too long'));
      return;
    }
    const legalDisclaimer = safeTrim(currentStyle.value.legalDisclaimer);
    if (legalDisclaimer.length > 600) {
      toast.error(t('agentStyles.validation.legalDisclaimerTooLong', 'Legal disclaimer is too long'));
      return;
    }
    const legalDisclaimerLabel = safeTrim(currentStyle.value.legalDisclaimerLabel);
    if (legalDisclaimerLabel.length > 60) {
      toast.error(t('agentStyles.validation.legalDisclaimerLabelTooLong', 'Legal disclaimer label is too long'));
      return;
    }
    const legalDisclaimerUrl = safeTrim(currentStyle.value.legalDisclaimerUrl);
    if (legalDisclaimerUrl.length > 0) {
      if (isValidHttpUrl(legalDisclaimerUrl) === false) {
        toast.error(t('agentStyles.validation.legalDisclaimerUrlInvalid', 'Legal disclaimer URL must be a valid http(s) URL'));
        return;
      }
    }
    const websiteAiAiButtonText = safeTrim(currentStyle.value.websiteAiAiButtonText);
    if (websiteAiAiButtonText.length > 30) {
      toast.error(t('agentStyles.validation.websiteAiAiButtonTextTooLong', 'AI button text is too long'));
      return;
    }
    const websiteAiWebsiteButtonText = safeTrim(currentStyle.value.websiteAiWebsiteButtonText);
    if (websiteAiWebsiteButtonText.length > 30) {
      toast.error(t('agentStyles.validation.websiteAiWebsiteButtonTextTooLong', 'Website button text is too long'));
      return;
    }
    const chatInputPlaceholder = safeTrim(currentStyle.value.chatInputPlaceholder);
    if (chatInputPlaceholder.length > 120) {
      toast.error(t('agentStyles.validation.chatInputPlaceholderTooLong', 'Chat input placeholder is too long'));
      return;
    }
    const messageUnderTextbox = safeTrim(currentStyle.value.messageUnderTextbox);
    if (messageUnderTextbox.length > 200) {
      toast.error(t('agentStyles.validation.messageUnderTextboxTooLong', 'Message under textbox is too long'));
      return;
    }
    const termsButtonText = safeTrim(currentStyle.value.termsButtonText);
    if (termsButtonText.length > 40) {
      toast.error(t('agentStyles.validation.termsButtonTextTooLong', 'Terms button text is too long'));
      return;
    }
    const floatingMessageText = safeTrim(currentStyle.value.floatingMessageText);
    if (floatingMessageText.length > 250) {
      toast.error(t('agentStyles.validation.floatingMessageTextTooLong', 'Floating message text is too long'));
      return;
    }
    const answerAnimationTexts = normalizeAnswerAnimationTexts(currentStyle.value.answerAnimationTexts);
    const answerAnimationIntervalMs = Number(currentStyle.value.answerAnimationIntervalMs);
    if (Number.isFinite(answerAnimationIntervalMs) === false || answerAnimationIntervalMs < 500 || answerAnimationIntervalMs > 10000) {
      toast.error(t('agentStyles.validation.answerAnimationIntervalInvalid', 'Answer animation interval must be between 500 and 10,000 milliseconds'));
      return;
    }
    if (currentStyle.value.styleType === 'Website + AI') {
      if (isValidHttpUrl(currentStyle.value.websiteAiUrl) === false) {
        toast.error(t('agentStyles.validation.websiteUrlInvalid', 'Website URL must be a valid http(s) URL'));
        return;
      }
    }

    const styleData = {
      styleName: safeTrim(currentStyle.value.stylename),
      cssStyles: css,
      logoBase64: currentStyle.value.logobase64,
      logoType: currentStyle.value.logoType,
      logoCode: currentStyle.value.logoCode,
      primaryColor: currentStyle.value.primaryColor,
      secondaryColor: currentStyle.value.secondaryColor,
      chatIconBase64: currentStyle.value.chatIconBase64,
      chatInputPlaceholder: chatInputPlaceholder,
      showMessageUnderTextbox: currentStyle.value.showMessageUnderTextbox,
      messageUnderTextbox: messageUnderTextbox,
      showTermsModal: currentStyle.value.showTermsModal,
      termsModalContent: safeTrim(currentStyle.value.termsModalContent),
      termsButtonText: termsButtonText,
      addShadows: currentStyle.value.addShadows === true,
      addPulse: currentStyle.value.addPulse === true,
      showFloatingMessage: currentStyle.value.showFloatingMessage === true,
      floatingMessageText: floatingMessageText,
      answerAnimationEnabled: currentStyle.value.answerAnimationEnabled === true,
      answerAnimationTexts: answerAnimationTexts,
      answerAnimationIntervalMs: Math.floor(answerAnimationIntervalMs),
      styleType: currentStyle.value.styleType,
      websiteAiFloatingButtonText: websiteAiFloatingButtonText,
      websiteAiFloatingButtonSubtitle: websiteAiFloatingButtonSubtitle,
      websiteAiTitle: websiteAiTitle,
      websiteAiSubtitle: websiteAiSubtitle,
      websiteAiDescription: websiteAiDescription,
      websiteAiShowMenu: currentStyle.value.websiteAiShowMenu === true,
      websiteAiShowAlert: currentStyle.value.websiteAiShowAlert === true,
      websiteAiAlertText: websiteAiAlertText,
      newConversationDescription: newConversationDescription,
      legalDisclaimer: legalDisclaimer,
      legalDisclaimerUrl: legalDisclaimerUrl,
      legalDisclaimerLabel: legalDisclaimerLabel,
      websiteAiAiButtonText: websiteAiAiButtonText,
      websiteAiWebsiteButtonText: websiteAiWebsiteButtonText,
      websiteAiUrl: safeTrim(currentStyle.value.websiteAiUrl)
    };

    if (isEditMode.value === true) {
      await axios.put(`${apiEndpoint}/api/styles/modifyAgentStyle/${currentStyle.value.styleid}`, styleData);
      toast.success(t('agentStyles.toasts.updateSuccess', 'Style updated successfully'));
    } else {
      await axios.post(`${apiEndpoint}/api/styles/createNewAgentStyle`, styleData);
      toast.success(t('agentStyles.toasts.createSuccess', 'Style created successfully'));
    }

    showCreateModal.value = false;
    loadStyles();
    resetForm();
  } catch (error) {
    logDevError('Error saving style:', error);
    toast.error(error.response?.data?.message !== undefined ? error.response?.data?.message : t('agentStyles.toasts.saveError', 'Failed to save style'));
  }
};

const handleEditClick = (styleid) => {
  const style = styles.value.find(s => s.styleid === styleid);
  if (style !== undefined) {
    currentStyle.value = {
      styleid: style.styleid,
      styleguid: style.styleguid,
      stylename: style.stylename,
      cssstyles: style.cssstyles,
      logobase64: style.logobase64,
      logoType: isDefinedAndNotEmpty(style.logotype) === true ? style.logotype : 'image',
      logoCode: isDefinedAndNotEmpty(style.logocode) === true ? style.logocode : '',
      primaryColor: style.primarycolor !== undefined ? style.primarycolor : '#000000',
      secondaryColor: style.secondarycolor !== undefined ? style.secondarycolor : '#ffffff',
      chatIconBase64: style.chaticonbase64 !== undefined ? style.chaticonbase64 : '',
      chatInputPlaceholder: style.chatinputplaceholder !== undefined && style.chatinputplaceholder !== null ? style.chatinputplaceholder : '',
      showMessageUnderTextbox: (typeof style.messageundertextbox === 'string' && style.messageundertextbox.trim() !== '') || style.showmessageundertextbox === true,
      messageUnderTextbox: style.messageundertextbox !== undefined && style.messageundertextbox !== null ? style.messageundertextbox : '',
      showTermsModal: (typeof style.termsmodalcontent === 'string' && style.termsmodalcontent.trim() !== '') || style.showtermsmodal === true,
      termsModalContent: style.termsmodalcontent !== undefined && style.termsmodalcontent !== null ? style.termsmodalcontent : '',
      termsButtonText: style.termsbuttontext !== undefined && style.termsbuttontext !== null ? style.termsbuttontext : '',
      addShadows: style.addshadows === true,
      addPulse: style.addpulse === true,
      showFloatingMessage: style.showfloatingmessage === true,
      floatingMessageText: style.floatingmessagetext !== undefined ? style.floatingmessagetext : '',
      answerAnimationEnabled: style.answeranimationenabled === true,
      answerAnimationTexts: normalizeAnswerAnimationTexts(style.answeranimationtexts),
      answerAnimationIntervalMs: Number.isFinite(Number(style.answeranimationintervalms)) === true ? Number(style.answeranimationintervalms) : 2600,
      styleType: style.styletype !== undefined ? style.styletype : 'Regular Chat',
      websiteAiFloatingButtonText: style.websiteaifloatingbuttontext !== undefined ? style.websiteaifloatingbuttontext : '',
      websiteAiFloatingButtonSubtitle: style.websiteaifloatingbuttonsubtitle !== undefined ? style.websiteaifloatingbuttonsubtitle : '',
      websiteAiTitle: style.websiteaititle !== undefined ? style.websiteaititle : '',
      websiteAiSubtitle: style.websiteaisubtitle !== undefined ? style.websiteaisubtitle : '',
      websiteAiDescription: style.websiteaidescription !== undefined ? style.websiteaidescription : '',
      websiteAiShowMenu: style.websiteaishowmenu !== false && style.websiteAiShowMenu !== false,
      websiteAiShowAlert: style.websiteaishowalert === true || style.websiteAiShowAlert === true,
      websiteAiAlertText: style.websiteaialerttext !== undefined ? style.websiteaialerttext : (style.websiteAiAlertText !== undefined ? style.websiteAiAlertText : ''),
      newConversationDescription: style.newconversationdescription !== undefined ? style.newconversationdescription : '',
      legalDisclaimer: style.legaldisclaimer !== undefined ? style.legaldisclaimer : '',
      legalDisclaimerUrl: style.legaldisclaimerurl !== undefined ? style.legaldisclaimerurl : '',
      legalDisclaimerLabel: style.legaldisclaimerlabel !== undefined ? style.legaldisclaimerlabel : '',
      websiteAiAiButtonText: style.websiteaiaibuttontext !== undefined ? style.websiteaiaibuttontext : '',
      websiteAiWebsiteButtonText: style.websiteaiwebsitebuttontext !== undefined ? style.websiteaiwebsitebuttontext : '',
      websiteAiUrl: style.websiteaiurl !== undefined ? style.websiteaiurl : ''
    };
    if (monacoEditorInstance !== null) {
      monacoEditorInstance.setValue(currentStyle.value.cssstyles);
    }
    isEditMode.value = true;
    styleNameError.value = false;
    styleNameErrorMessage.value = '';
    showCreateModal.value = true;
    nextTick(() => {
      if (editorContainer.value !== null) {
        handleResize();
      }
    });
  }
};

const handleDeleteClick = (styleId) => {
  if (styleId > 0) {
    selectedStyleId.value = styleId;
    isDeleteModalOpen.value = true;
  }
};

const deleteStyleConfirmed = async () => {
  try {
    await axios.delete(`${apiEndpoint}/api/styles/deleteAgentStyle/${selectedStyleId.value}`);
    toast.success(t('agentStyles.toasts.deleteSuccess', 'Style deleted successfully'));
    loadStyles();
    isDeleteModalOpen.value = false;
    selectedStyleId.value = null;
  } catch (error) {
    logDevError('Error deleting style:', error);
    toast.error(t('agentStyles.toasts.deleteError', 'Failed to delete style'));
  }
};

const handleResize = () => {
  if (resizeTimeout > 0) {
    clearTimeout(resizeTimeout);
  }
  
  resizeTimeout = setTimeout(() => {
    if (editorContainer.value !== null) {
      nextTick();
    }
  }, 100);
};

const handleResizeError = (event) => {
  if (event.message === 'ResizeObserver loop completed with undelivered notifications.') {
    event.stopImmediatePropagation();
  }
};

onMounted(() => {
  loadStyles();
  loadStyle(`
    html[dir="rtl"] button.mr-2 { margin-left: 0.5rem; margin-right: 0; }
    html[dir="rtl"] button.ml-2 { margin-right: 0.5rem; margin-left: 0; }
    html[dir="rtl"] button.mr-4 { margin-left: 1rem; margin-right: 0; }
    html[dir="rtl"] button.ml-4 { margin-right: 1rem; margin-left: 0; }
    .vue3-easy-data-table {
      height: 95%;
    }
  `);
  window.addEventListener('resize', handleResize);
  window.addEventListener('error', handleResizeError);
});

function loadStyle(cssString) {
  const style = document.createElement('style');
  style.type = 'text/css';

  if (style.styleSheet != null) {
    style.styleSheet.cssText = cssString;
  } else {
    style.appendChild(document.createTextNode(cssString));
  }

  document.head.appendChild(style);
}

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('error', handleResizeError);
  if (resizeTimeout !== null) {
    clearTimeout(resizeTimeout);
  }
});

watch(() => showCreateModal.value, async (newVal) => {
  if (newVal === true && editorContainer.value !== null) {
    await nextTick();
    handleResize();
  }
});

const handleCreateClick = () => {
  resetForm();
  showCreateModal.value = true;
};

const closeModal = () => {
  showCreateModal.value = false;
  resetForm();
};

let isMonacoLoading = false;
const initMonaco = async () => {
  if (monacoContainer.value !== null && monacoEditorInstance === null && isMonacoLoading === false) {
    isMonacoLoading = true;
    try {
      const monaco = await loader.init();
      monacoEditorInstance = monaco.editor.create(monacoContainer.value, {
        value: currentStyle.value.cssstyles,
        language: 'css',
        theme: 'vs-dark',
        automaticLayout: true, 
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
      });

      monacoEditorInstance.onDidChangeModelContent(() => {
        if (monacoEditorInstance !== null) {
          currentStyle.value.cssstyles = monacoEditorInstance.getValue();
        }
      });
    } catch (error) {
      logDevError("Failed to initialize Monaco editor:", error);
      toast.error(t('agentStyles.toasts.editorLoadError', "Could not load CSS editor."));
    } finally {
       isMonacoLoading = false;
    }
  }
};

const destroyMonaco = () => {
  if (monacoEditorInstance !== null) {
    monacoEditorInstance.dispose();
    monacoEditorInstance = null;
  }
};

watch(() => showCreateModal.value, (newVal) => {
  if (newVal === true) {
    nextTick(() => {
      initMonaco(); 
    });
  } else {
    destroyMonaco();
  }
});
watch(() => isLightIDE.value, async (newVal) => {
  if (newVal === true) {
    destroyMonaco();
  }
  if (newVal === false) {
    await nextTick();
    initMonaco();
  }
})

onUnmounted(() => {
  destroyMonaco(); 
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('error', handleResizeError);
  if (resizeTimeout !== null) {
    clearTimeout(resizeTimeout);
  }
});

const handleAIGenerateClick = () => {
  resetForm();
  showAIGenerateModal.value = true;
};
</script>

<style>
.badge-error, .btn-error, .badge-success, .btn-success {
  color: #fff;
}
table {
  font-family: "Heebo", sans-serif !important;
}
.scroll-content {
  height: 100%;
}
.vue3-easy-data-table {
  height: 95%;
}

#webpack-dev-server-client-overlay[data-message*="ResizeObserver loop completed with undelivered notifications"] {
  display: none !important;
}

.code-editor-container {
  height: 300px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1rem;
  background-color: #ffffff;
  width: 100% !important;
  position: relative;
  display: flex;
  flex-direction: column;
}
.code-editor-container :deep(.editor-header) {
  background-color: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
}
.code-editor-container :deep(.editor-content) {
  flex: 1;
  overflow: auto;
  width: 100%;
  min-height: 0;
}
.code-editor-container :deep(.line-numbers) {
  background-color: #f9fafb;
  color: #6b7280;
}
.code-editor-container :deep(.editor-textarea) {
  color: #1f2937;
  background-color: #ffffff;
}

.modal-box {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-box > div:not(.modal-action) {
  overflow-y: auto;
  flex-grow: 1;
}

.tab-content {
  min-height: 300px;
}

.sticky {
  position: sticky;
  background-color: inherit;
}

.checkbox {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.checkbox:checked {
  background-color: var(--primary);
  border-color: var(--primary);
}

.checkbox:focus {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.checkbox:hover {
  border-color: var(--primary);
}
.monaco-editor-container {
  direction: ltr !important;
  unicode-bidi: embed !important;
}

.agent-style-modal-header {
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.dark .agent-style-modal-header {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.agent-style-modal-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.agent-style-modal-footer {
  padding-top: 0.75rem;
  padding-bottom: 0.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  justify-content: flex-end;
}

.dark .agent-style-modal-footer {
  border-top-color: rgba(255, 255, 255, 0.12);
}

#agent-style-modal .modal-action .btn + .btn {
  margin-left: 0.5rem;
}

html[dir="rtl"] #agent-style-modal .modal-action .btn + .btn {
  margin-left: 0;
  margin-right: 0.5rem;
}

.agent-form > * + * {
  margin-top: 1rem;
}

.agent-row {
  display: flex;
  flex-direction: column;
}

.agent-row > * + * {
  margin-top: 1rem;
}

@media (min-width: 768px) {
  .agent-row {
    flex-direction: row;
  }
  .agent-row > * {
    flex: 1 1 0%;
    min-width: 0;
  }
  .agent-row > * + * {
    margin-top: 0;
    margin-left: 1rem;
  }
  html[dir="rtl"] .agent-row > * + * {
    margin-left: 0;
    margin-right: 1rem;
  }
}

.agent-section {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.015);
}

.dark .agent-section {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: rgba(255, 255, 255, 0.03);
}

.agent-section-title {
  font-weight: 600;
  color: inherit;
  margin-bottom: 0.75rem;
}

.agent-check-text {
  margin-left: 0.5rem;
}

html[dir="rtl"] .agent-check-text {
  margin-left: 0;
  margin-right: 0.5rem;
}
</style> 